import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  ExtendedFlex,
  ExtendedBox,
  Hero,
  ModalWithOverlay,
  ScrollToTopOnMount,
  CarouselLarge,
  Numeral,
  Body,
} from '../coreui';

import ConstantStaticData from './data';
import DynamicBrowseJoinLink from '../helpers';
import heroBannerImage from './assets/img_hero_whyreits.jpg';
import ReitInvesting from './reitInvesting';
import ChartAndCopy from '../benefits/components/chartAndCopy';
import RelatedQueueContent from '../knowledgeCenter/components/RelatedQueueContent';
import ComparingReits from './comparingReits';
import InvestmentObjectives from './investmentObjectives';
import InvestToday from './components/InvestToday';
import IconCallouts from '../components/IconCallouts';
import Graph from './assets/graph_history.png';
import AvailableToday from '../benefits/components/availableToday';
import Section from '../ioDetails/components/section';

import imgMr1Returns from './assets/img_returnsMRI.png';
import imgMr2Returns from './assets/img_returnsMRII.png';
import CarouselContent from '../components/CarouselContent';
import BrowseLink from '../components/BrowseLink';

class WhyReits extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalVisible: false,
      modalContent: null,
    };

    this.toggleModal = this.toggleModal.bind(this);
  }

  toggleModal(modalContent) {
    this.setState({
      isModalVisible: !this.state.isModalVisible,
      modalContent: this.state.isModalVisible ? null : modalContent,
    });
  }

  render() {
    const {
      isModalVisible,
      modalContent,
    } = this.state;
    const {
      user,
      isLoggedIn,
      reitData,
      mogulReit1DistributionDuration,
      mogulReit1DistributionAmount,
      mogulReit1DistributionTooltip,
      mogulReit1EffectiveDistributionDate,
      mogulReit2DistributionDuration,
      mogulReit2DistributionAmount,
      mogulReit2DistributionTooltip,
      mogulReit2EffectiveDistributionDate,
    } = this.props;

    return (
      <ExtendedBox>
        <ScrollToTopOnMount />
        <ExtendedFlex>
          <Hero
            image={heroBannerImage}
            header="ACCESS THE FULL POTENTIAL OF COMMERCIAL REAL ESTATE"
            subHeader="Investing in real estate investment trusts (&#34;REITS&#34;)"
            bgPosition="74%"
          />
        </ExtendedFlex>
        <IconCallouts
          {...ConstantStaticData.basics}
          moreComponent={(
            <ExtendedFlex>
              <DynamicBrowseJoinLink
                isLoggedIn={isLoggedIn}
                anonText="Get Started"
                type="L1"
              />
            </ExtendedFlex>
          )}
          bg="gray.0"
        />
        <InvestmentObjectives />
        <ComparingReits
          toggleModal={this.toggleModal}
        />
        <ChartAndCopy
          toggleModal={this.toggleModal}
          {...ConstantStaticData.performanceHistory}
          chart={Graph}
          backgroundColor="white"
          isLoggedIn={user.isLoggedIn}
        />
        <AvailableToday
          {...{
            ...ConstantStaticData.reitData,
            investments: reitData,
          }}
          user={user}
        />
        <Section>
          <CarouselLarge>
            <CarouselContent
              heading="MogulREIT I"
              subheading="Our REIT distributions to date"
              body={mogulReit1DistributionDuration ? (
                <>
                  Since inception, MogulREIT I has distributed {''}
                  {mogulReit1DistributionDuration} of consecutive distributions
                  to investors totaling over {(
                    <Numeral
                      value={mogulReit1DistributionAmount}
                      format="$0.0[0]a"
                    />
                  )}.
                </>
              ) : undefined}
              cta={(
                <BrowseLink
                  isLoggedIn={isLoggedIn}
                />
              )}
              image={imgMr1Returns}
              imageContainerProps={{
                justify: 'center',
              }}
              caption={`Annualized distributions (net of fees) as of ${(
                moment.unix(mogulReit1EffectiveDistributionDate).format('M/D/YY')
              )}`}
              disclaimer
              handleOnClick={() => {
                this.toggleModal((
                  <Body
                    type="B1"
                  >
                    {mogulReit1DistributionTooltip}
                  </Body>
                ));
              }}
            />
            <CarouselContent
              heading="MogulREIT II"
              subheading="Our REIT distributions to date"
              body={mogulReit2DistributionDuration ? (
                <>
                  Since inception, MogulREIT II has distributed {''}
                  {mogulReit2DistributionDuration} of consecutive distributions
                  to investors totaling over {(
                    <Numeral
                      value={mogulReit2DistributionAmount}
                      format="$0.0[0]a"
                    />
                  )}.
                </>
              ) : undefined}
              cta={(
                <BrowseLink
                  isLoggedIn={isLoggedIn}
                />
              )}
              image={imgMr2Returns}
              imageContainerProps={{
                justify: 'center',
              }}
              caption={`Annualized distributions (net of fees) since ${(
                moment.unix(mogulReit2EffectiveDistributionDate).format('M/D/YY')
              )}`}
              disclaimer
              handleOnClick={() => {
                this.toggleModal((
                  <Body
                    type="B1"
                  >
                    {mogulReit2DistributionTooltip}
                  </Body>
                ));
              }}
            />
          </CarouselLarge>
        </Section>
        <ReitInvesting />
        <RelatedQueueContent
          title="Gain New Investing Insight"
          queue="why_reits_articles"
        />
        <InvestToday
          isLoggedIn={user.isLoggedIn}
        />
        <ModalWithOverlay
          isVisible={isModalVisible}
          handleClick={this.toggleModal}
        >
          {modalContent}
        </ModalWithOverlay>
      </ExtendedBox>
    );
  }
}

const mapStateToProps = state => ({
  user: state.homepage.user,
  isLoggedIn: state.app.user.isLoggedIn,
  reitData: state.reitData.list,
  mogulReit1DistributionDuration: state.tokens.mogulReit1DistributionDuration,
  mogulReit1DistributionAmount: state.tokens.mogulReit1DistributionAmount,
  mogulReit1DistributionTooltip: state.tokens.mogulReit1DistributionTooltip,
  mogulReit1EffectiveDistributionDate: state.tokens.mogulReit1EffectiveDistributionDate,
  mogulReit2DistributionDuration: state.tokens.mogulReit2DistributionDuration,
  mogulReit2DistributionAmount: state.tokens.mogulReit2DistributionAmount,
  mogulReit2DistributionTooltip: state.tokens.mogulReit2DistributionTooltip,
  mogulReit2EffectiveDistributionDate: state.tokens.mogulReit2EffectiveDistributionDate,
});

WhyReits.propTypes = {
  user: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  reitData: PropTypes.array,
  mogulReit1LastDistributionDeclared: PropTypes.number,
  mogulReit2LastDistributionDeclared: PropTypes.number,
};

export default connect(mapStateToProps)(WhyReits);
